import React, { useMemo, useContext } from 'react';
import { isTreeRoot } from 'flow/service/rules';
import type { GroupRule, RuleBuilderRule } from 'flow/service/rules';
import RuleTemplate from '../RuleTemplate';
import { StyledGroupContainer, StyledGroupParenthesisContainer } from './styles';
import { useAppDispatch } from '~/store/reduxHooks';
import { setRuleBuilderHighlightedRuleId } from 'flow/store/redux/slice';
import ParenthesisSelect from './ParenthesisSelect';
import { hasAmbiguousChildren } from './helpers';
import { GroupContext } from './context';
import { RuleBuilderContext } from '../../context';

type GroupProps = {
  rule: GroupRule;
  // Alias for <Rule /> to avoid a dependency cycle
  RuleWrapper: React.ElementType<{ rule: RuleBuilderRule }>;
};

export default function Group({ rule, RuleWrapper }: GroupProps) {
  const dispatch = useAppDispatch();
  const { children } = rule.data;
  const { containerType } = useContext(RuleBuilderContext);

  // We render invalid treatment on all siblings of an ambiguous rule
  const groupContext = useMemo(() => ({
    hasAmbiguousRules: hasAmbiguousChildren(rule),
  }), [rule]);

  const builtChildren = children.map(child => <RuleWrapper key={child.id} rule={child} />);

  if (rule.data.isAmbiguous) {
    return (
      <StyledGroupContainer>
        {builtChildren}
      </StyledGroupContainer>
    );
  }

  if (isTreeRoot(rule)) {
    return (
      <StyledGroupContainer>
        <GroupContext.Provider value={groupContext}>
          {builtChildren}
        </GroupContext.Provider>
      </StyledGroupContainer>
    );
  }

  const onMouseHover = (payload: RuleBuilderRule['id'] | null) => (
    dispatch(setRuleBuilderHighlightedRuleId(payload))
  );

  return (
    <StyledGroupContainer>
      <GroupContext.Provider value={groupContext}>
        <RuleTemplate rule={rule} groupPosition="start">
          <StyledGroupParenthesisContainer
            onMouseEnter={() => onMouseHover(rule.id)}
            onMouseLeave={() => onMouseHover(null)}
            containerType={containerType}
          >
            <ParenthesisSelect rule={rule} groupPosition="start" />
          </StyledGroupParenthesisContainer>
        </RuleTemplate>

        {builtChildren}

        <RuleTemplate rule={rule} groupPosition="end">
          <StyledGroupParenthesisContainer
            onMouseEnter={() => onMouseHover(rule.id)}
            onMouseLeave={() => onMouseHover(null)}
            containerType={containerType}
          >
            <ParenthesisSelect rule={rule} groupPosition="end" />
          </StyledGroupParenthesisContainer>
        </RuleTemplate>
      </GroupContext.Provider>
    </StyledGroupContainer>
  );
}
